import { useTranslation } from 'react-i18next';
/**
 * AutoBackupCard — Settings panel for the scheduled Google Drive
 * auto-backup feature.
 *
 * Renders inside `DatabaseBackupTab` only when Drive is connected. Admins
 * can:
 *   • Toggle auto-backup on/off
 *   • Choose the interval (1 – 720 hours)
 *   • Choose the retention count (1 – 365 auto-backups)
 *   • See the last-run timestamp + status, next scheduled run, and any
 *     error message from the last tick.
 *
 * IMPORTANT — manual backups are NEVER deleted by retention. The cleanup
 * routine on the backend strictly matches the `expertfix_backup_auto_`
 * filename prefix. The card surfaces this guarantee in copy so admins
 * trust the toggle.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { CalendarClock, Save, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { dateLocale } from '../lib/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(dateLocale()); } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  if (status === 'success') {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-300" data-testid="autobackup-status">
        <CheckCircle2 className="h-3 w-3 mr-1" /> success
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge className="bg-red-100 text-red-800 border-red-300" data-testid="autobackup-status">
        <AlertTriangle className="h-3 w-3 mr-1" /> failed
      </Badge>
    );
  }
  if (status === 'skipped_disconnected') {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="autobackup-status">
        <AlertTriangle className="h-3 w-3 mr-1" /> {t('auto.set.driveDisconnected')}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" data-testid="autobackup-status">
      {t('auto.set.notYetRun')}
    </Badge>
  );
};

export default function AutoBackupCard({ driveConnected, onChange }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({
    enabled: false, interval: 24, retention: 14,
  });
  const dirty = cfg && (
    cfg.auto_backup_enabled !== draft.enabled
    || cfg.auto_backup_interval_hours !== draft.interval
    || cfg.auto_backup_retention !== draft.retention
  );

  const fetchCfg = async () => {
    try {
      const r = await axios.get(`${API}/drive/auto-backup/settings`);
      setCfg(r.data);
      setDraft({
        enabled: r.data.auto_backup_enabled,
        interval: r.data.auto_backup_interval_hours,
        retention: r.data.auto_backup_retention,
      });
    } catch (e) {
      // Non-admins get 403 — render nothing in that case (handled below)
      setCfg({ unauthorized: true });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchCfg(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/drive/auto-backup/settings`, {
        auto_backup_enabled: draft.enabled,
        auto_backup_interval_hours: draft.interval,
        auto_backup_retention: draft.retention,
      });
      setCfg(r.data);
      toast.success(draft.enabled ? t('auto.set.autoBackupEnabled') : t('auto.set.autoBackupSettingsSaved'));
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-4 border rounded-md p-4 bg-white text-sm text-gray-500">
        Loading auto-backup settings…
      </div>
    );
  }
  if (cfg?.unauthorized) return null;

  return (
    <Card className="mt-4 border-blue-200 bg-blue-50/30" data-testid="autobackup-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-blue-600" />
          {t('auto.set.scheduledAutoBackup')}
          {cfg.auto_backup_enabled && (
            <Badge className="bg-green-100 text-green-800 border-green-300 ml-1">on</Badge>
          )}
        </CardTitle>
        <CardDescription className="text-xs">
          {t('auto.set.theServerRunsABackupAt')}
          {cfg.configured_by && (
            <> {t('auto.set.currentlyBoundTo')} <strong>{cfg.configured_by}</strong>&apos;s Drive token.</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Settings grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Toggle */}
          <div className="flex items-center gap-3 p-3 bg-white rounded border">
            <Switch
              data-testid="autobackup-toggle"
              checked={draft.enabled}
              onCheckedChange={(v) => setDraft((d) => ({ ...d, enabled: v }))}
              disabled={!driveConnected}
            />
            <div>
              <Label className="text-xs font-medium">{t('auto.set.autoBackupEnabled')}</Label>
              <div className="text-[11px] text-gray-500">
                {driveConnected ? t('auto.set.toggleToScheduleRecurringBackups') : t('auto.set.connectDriveFirst')}
              </div>
            </div>
          </div>

          {/* Interval */}
          <div className="p-3 bg-white rounded border">
            <Label htmlFor="ab-interval" className="text-xs font-medium">
              {t('auto.set.intervalHours')}
            </Label>
            <Input
              id="ab-interval"
              type="number"
              min={1}
              max={720}
              value={draft.interval}
              onChange={(e) => setDraft((d) => ({ ...d, interval: Math.max(1, parseInt(e.target.value, 10) || 24) }))}
              data-testid="autobackup-interval"
              className="mt-1 h-8"
            />
            <div className="text-[11px] text-gray-500 mt-1">
              {draft.interval === 24 ? 'Daily' : draft.interval === 168 ? 'Weekly' : `Every ${draft.interval}h`}
            </div>
          </div>

          {/* Retention */}
          <div className="p-3 bg-white rounded border">
            <Label htmlFor="ab-retention" className="text-xs font-medium">
              {t('auto.set.keepLastNAutoBackups')}
            </Label>
            <Input
              id="ab-retention"
              type="number"
              min={1}
              max={365}
              value={draft.retention}
              onChange={(e) => setDraft((d) => ({ ...d, retention: Math.max(1, parseInt(e.target.value, 10) || 14) }))}
              data-testid="autobackup-retention"
              className="mt-1 h-8"
            />
            <div className="text-[11px] text-gray-500 mt-1">
              {t('auto.set.olderAutoBackupsBeyondThisCount')}
            </div>
          </div>
        </div>

        {/* Safety notice — surfaces the manual-backup guarantee */}
        <Alert className="bg-emerald-50 border-emerald-200">
          <ShieldCheck className="h-4 w-4 text-emerald-700" />
          <AlertDescription className="text-emerald-900 text-xs">
            <strong>{t('auto.set.manualBackupsAreNeverAutoDeleted')}</strong> Retention only prunes
            files named <code className="bg-emerald-100 px-1 rounded">{t('auto.set.expertfixBackupAuto')}</code> —
            your manual &quot;Backup to Drive Now&quot; files are safe forever.
          </AlertDescription>
        </Alert>

        {/* Status panel */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div className="bg-white p-2 rounded border">
            <Label className="text-[10px] text-gray-500 uppercase">{t('auto.set.lastRun')}</Label>
            <div className="font-medium mt-0.5" data-testid="autobackup-last-run">
              {formatDate(cfg.auto_backup_last_run_at)}
            </div>
          </div>
          <div className="bg-white p-2 rounded border">
            <Label className="text-[10px] text-gray-500 uppercase">{t('auto.set.status')}</Label>
            <div className="mt-0.5">
              <StatusBadge status={cfg.auto_backup_last_status} />
            </div>
          </div>
          <div className="bg-white p-2 rounded border">
            <Label className="text-[10px] text-gray-500 uppercase">{t('auto.set.nextRun')}</Label>
            <div className="font-medium mt-0.5" data-testid="autobackup-next-run">
              {cfg.auto_backup_enabled ? formatDate(cfg.next_run_at) : '—'}
            </div>
          </div>
          <div className="bg-white p-2 rounded border">
            <Label className="text-[10px] text-gray-500 uppercase">{t('auto.set.lastFile')}</Label>
            <div className="font-medium truncate mt-0.5" title={cfg.auto_backup_last_file_name || ''}>
              {cfg.auto_backup_last_file_name || '—'}
            </div>
          </div>
        </div>

        {/* Error / disconnected banner */}
        {cfg.auto_backup_last_status === 'skipped_disconnected' && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <AlertDescription className="text-amber-900 text-xs">
              The last auto-backup was skipped because your Drive token isn&apos;t usable.
              Reconnect Google Drive above, then click <em>{t('auto.set.save')}</em> here to re-enable.
            </AlertDescription>
          </Alert>
        )}
        {cfg.auto_backup_last_status === 'failed' && cfg.auto_backup_last_error && (
          <Alert className="bg-red-50 border-red-200">
            <AlertTriangle className="h-4 w-4 text-red-700" />
            <AlertDescription className="text-red-900 text-xs">
              Last attempt failed: <code className="bg-red-100 px-1 rounded">{cfg.auto_backup_last_error}</code>
            </AlertDescription>
          </Alert>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={save}
            disabled={saving || !dirty}
            data-testid="autobackup-save"
          >
            <Save className="h-3 w-3 mr-1" />
            {saving ? t('auto.set.saving2') : t('auto.set.saveSettings')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
